import { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClickOutside } from '../../hooks/useClickOutside';

interface UserMenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

interface Props {
  firstName: string;
  lastName: string;
  items: UserMenuItem[];
  onLogout: () => void;
  transparent?: boolean;
}

export function UserMenu({ firstName, lastName, items, onLogout, transparent }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const displayName = `${firstName} ${lastName.charAt(0)}.`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      const firstItem = ref.current?.querySelector<HTMLElement>('a, button[role="menuitem"]');
      firstItem?.focus();
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center gap-2 transition-colors ${
          transparent ? 'text-white/90 hover:text-white' : 'text-slate-600 hover:text-primary'
        }`}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
          transparent
            ? 'bg-white/20 text-white border border-white/30'
            : 'bg-primary/10 text-primary border border-primary/20'
        }`}>
          {initials}
        </div>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg shadow-elevated border border-slate-200 py-1 animate-fade-in-fast z-50"
        >
          <div className="px-4 py-2.5 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-900">{displayName}</p>
          </div>

          {items.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                role="menuitem"
                onClick={close}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'text-primary bg-primary/5 font-semibold'
                    : 'text-slate-600 hover:text-primary hover:bg-slate-50'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}

          <div className="border-t border-slate-100 mt-1 pt-1">
            <button
              role="menuitem"
              onClick={() => { close(); onLogout(); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
